import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { inventorySparesTableColumnsConfig } from "./inventorySparesTableColumnDef";
import { Table } from "../../../../component/table/Table";

import { toast } from "react-toastify";
import { inventorySparesDownloadRequest } from "../../../../http-request/inventory/inventoryDownloadFile";
import { InventorySpareForm } from "../../../../component/inventory/spares/inventorySpareForm/InventorySpareForm";
import { Modal } from "../../../../component/inventory/spares/modal/Modal";
import {
  useApproveRequestMutation,
  useRejectRequestMutation,
} from "../../../../services/inventory/spares/inventorySpareMutationSlice";

export const InventorySparesTablePage = ({ data, refetch }) => {
  const [columnDefs, setColumnDefs] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const [rejectRequest] = useRejectRequestMutation();
  const [approveRequest] = useApproveRequestMutation();

  /* -------- handlers required by column config -------- */

  const handleNavigateToLotList = useCallback(
    (requestId) => {
      navigate(`/inventory/spares/${requestId}`);
    },
    [navigate]
  );

  const handleDownload = useCallback(async (requestId) => {
    try {
      toast.success("downloading...");
      const fileData = await inventorySparesDownloadRequest({
        category: "spares",
        requestId: requestId,
      });

      const contentType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([fileData], { type: contentType });

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `mobiGarage_${requestId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error occurred during download:", err);
      toast.success("error while downloading");
    }
  }, []);

  const handleOpenModal = useCallback((rowData) => {
    setSelectedRow(rowData);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
  };

  const handleReject = async (formData) => {
    if (!selectedRow) return;

    const toastId = toast.loading("Rejecting request...");

    try {
      setIsSubmitting(true);

      const response = await rejectRequest({
        request_id: selectedRow.request_id,
        remarks: formData.remarks,
      }).unwrap();
      refetch();

      toast.update(toastId, {
        render: response?.message?.displayMessage || "Rejected successfully",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });

      handleCloseModal();
    } catch (error) {
      toast.update(toastId, {
        render:
          error?.data?.message?.displayMessage || "Failed to reject request",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* -------------------- APPROVE -------------------- */
  const handleApprove = async () => {
    if (!selectedRow) return;

    const toastId = toast.loading("Approving request...");

    try {
      setIsSubmitting(true);

      const response = await approveRequest({
        request_id: selectedRow.request_id,
      }).unwrap();

      refetch();

      toast.update(toastId, {
        render: response?.message?.displayMessage || "Approved successfully",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });

      handleCloseModal();
    } catch (error) {
      toast.update(toastId, {
        render:
          error?.data?.message?.displayMessage || "Failed to approve request",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* -------- build columns once -------- */

  useEffect(() => {
    setColumnDefs(
      inventorySparesTableColumnsConfig(
        handleOpenModal,
        handleDownload,
        handleNavigateToLotList
      )
    );
  }, [handleOpenModal, handleDownload, handleNavigateToLotList]);

  return (
    <>
      <Table data={data} columns={columnDefs} />
      <Modal
        isOpen={isModalOpen}
        title="Approve or Reject Spare Request"
        onClose={handleCloseModal}
      >
        <InventorySpareForm
          initialValues={selectedRow}
          onSubmit={handleReject}
          onApprove={handleApprove}
          isSubmitting={isSubmitting}
        />
      </Modal>
    </>
  );
};
